import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconExternalLink,
  IconLoader2,
} from '@tabler/icons-react';
import Markdown from '../../components/Markdown';
import { dxBoardAPI } from '../../api/client';
import { COLORS, RADIUS, badge, btn } from '../../utils/uiStyles';
import { safeUrl } from '../../utils/safeUrl';

const SYSTEMS = ['샵메이트 1.0', '샵메이트 2.0'];
const SECTION_META = [
  { key: 'new', label: '🆕 신규', tone: 'info' },
  { key: 'next_week', label: '📅 다음 주 예정', tone: 'info' },
  { key: 'delayed', label: '⚠️ 지연', tone: 'danger' },
  { key: 'on_hold', label: '⏸ 보류', tone: 'default' },
  { key: 'deployed', label: '🚀 배포(완료)', tone: 'success' },
  { key: 'changed', label: '✏️ 변경', tone: 'default' },
  { key: 'issues', label: '🔴 이슈', tone: 'danger' },
];
const SECTION_LIMIT = 8;
const SYNC_POLL_INTERVAL_MS = 5000;
const SYNC_POLL_MAX_ATTEMPTS = 20;
const ACTION_KPI = [
  { key: 'delayed', label: '⚠️ 지연', tone: 'danger' },
  { key: 'next_week', label: '📅 다음주', tone: 'info' },
  { key: 'reassign', label: '🔁 재배정', tone: 'warn' },
  { key: 'issues', label: '🔴 이슈', tone: 'danger' },
];
const SECONDARY_KPI = [
  { key: 'new', label: '신규' },
  { key: 'deployed', label: '배포' },
  { key: 'on_hold', label: '보류' },
  { key: 'changed', label: '변경' },
  { key: 'qa_open', label: 'QA' },
];

const sectionStyle = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.borderSoft}`,
  borderRadius: RADIUS.lg,
  padding: '15px 16px',
};

function formatDate(value) {
  if (!value) return '-';
  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (parts) return `${parts[2]}/${parts[3]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

const CARD_DATE_FIELDS = [
  { label: '생성', key: 'created_at' },
  { label: '시작', key: 'dev_start_at' },
  { label: '개발완료', key: 'dev_done_at' },
  { label: 'QA완료', key: 'qa_done_at' },
  { label: '배포', key: 'deploy_planned_at' },
];

function CardDateLine({ card }) {
  const text = CARD_DATE_FIELDS.map(({ label, key }) => `${label} ${formatDate(card?.[key])}`).join(' · ');
  return <span style={{ color: COLORS.textMuted, minWidth: 0, wordBreak: 'keep-all' }}>{text}</span>;
}

function formatSyncedAt(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const TONE_MAP = { default: 'gray', info: 'blue', success: 'green', warn: 'yellow', danger: 'red' };

function badgeStyle(tone = 'default') {
  return { ...badge(TONE_MAP[tone] || 'gray') };
}

function statusTone(status) {
  if (status === '완료') return 'success';
  if (status === '보완요청') return 'danger';
  if (status === '배포요청') return 'info';
  if (status === '보류') return 'warn';
  return 'default';
}

function reqTypeTone(reqType) {
  if (reqType === '버그') return 'danger';
  if (reqType === 'CS수정') return 'warn';
  if (reqType === '신규' || reqType === '개선') return 'info';
  return 'default';
}

function formatReqTypeCoverage(coverage) {
  const typed = Number(coverage?.typed || 0);
  const total = Number(coverage?.total || 0);
  const percent = total ? Math.round((typed / total) * 100) : 0;
  return `유형 표기 커버리지 ${typed.toLocaleString()}/${total.toLocaleString()}(${percent}%)`;
}

function kpiValue(data, key) {
  if (data?.kpi && data.kpi[key] !== undefined) return Number(data.kpi[key] || 0);
  if (key === 'reassign') return Number(data?.reassign_progress_count || 0);
  return 0;
}

function KpiBand({ data }) {
  const secondaryText = SECONDARY_KPI.map((item) => `${item.label} ${kpiValue(data, item.key).toLocaleString()}`).join(' · ');
  return (
    <div style={{ marginTop: '9px', display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
      {ACTION_KPI.map((item) => {
        const value = kpiValue(data, item.key);
        return (
          <span key={item.key} style={badgeStyle(value ? item.tone : 'default')}>
            {item.label} {value.toLocaleString()}
          </span>
        );
      })}
      <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 700, lineHeight: 1.5 }}>{secondaryText}</span>
    </div>
  );
}

function assigneeText(card) {
  const names = (card.assignees || []).map((item) => item?.name).filter(Boolean);
  return names.length ? names.join(' · ') : '개발자 없음';
}

function changeValue(value) {
  return value || '(없음)';
}

function ChangeDetails({ card }) {
  const changes = Array.isArray(card.changes) ? card.changes : [];
  if (changes.length) {
    return (
      <div style={{ marginTop: '4px', color: COLORS.textSub, fontSize: '12px', lineHeight: 1.45, wordBreak: 'break-word' }}>
        {changes.map((change, idx) => (
          <div key={`${change.field_label || 'change'}-${idx}`}>
            {change.field_label || '변경'}: {changeValue(change.old)} → {changeValue(change.new)}
          </div>
        ))}
      </div>
    );
  }
  if (card.changes_unavailable) {
    return (
      <div style={{ marginTop: '4px', color: COLORS.textMuted, fontSize: '12px', lineHeight: 1.45 }}>
        변경됨 · 이번 주부터 상세 변경이력 축적(그 전은 변경 여부만)
      </div>
    );
  }
  return null;
}

function nextWeekBadgeLabel(card) {
  if (card.next_week_label) return card.next_week_label;
  if (card.deploy_planned_at) return '배포예정';
  if (card.status === '진행 중' && card.dev_done_at) return '개발완료예정';
  if (card.status === 'QA 진행' && card.qa_done_at) return 'QA완료예정';
  return '예정';
}

function CardRow({ card, index, sectionKey }) {
  const href = safeUrl(card.url);
  const delay = Number(card.delay_days || 0);
  const showReason = sectionKey === 'issues' && card.reason;
  const showChanges = sectionKey === 'changed';
  const showNextWeek = sectionKey === 'next_week';
  return (
    <li style={{ borderTop: index === 0 ? 'none' : `1px solid ${COLORS.borderSoft}`, padding: '10px 0', minWidth: 0 }}>
      <div style={{ display: 'flex', gap: '8px', minWidth: 0 }}>
        <span style={{ width: '22px', flexShrink: 0, textAlign: 'right', color: COLORS.textMuted, fontSize: '12px', fontWeight: 620, lineHeight: 1.55 }}>{index + 1}.</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
            {href ? (
              <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.text, textDecoration: 'none', fontWeight: 620, fontSize: '13px', lineHeight: 1.45, display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <span style={{ wordBreak: 'break-word' }}>{card.title || '(제목 없음)'}</span>
                <IconExternalLink size={13} stroke={1.8} style={{ flexShrink: 0 }} />
              </a>
            ) : (
              <span style={{ color: COLORS.text, fontWeight: 620, fontSize: '13px', lineHeight: 1.45, wordBreak: 'break-word' }}>{card.title || '(제목 없음)'}</span>
            )}
            {delay > 0 && <span style={badgeStyle('danger')}>D+{delay}</span>}
            <span style={badgeStyle(statusTone(card.status))}>{card.status || '상태 없음'}</span>
            {showNextWeek && <span style={badgeStyle('info')}>{nextWeekBadgeLabel(card)}</span>}
            {showNextWeek && card.next_week_due_at && <span style={badgeStyle('default')}>{formatDate(card.next_week_due_at)}</span>}
            {showChanges && card.has_status_transition && <span style={badgeStyle('info')}>상태전이</span>}
            {card.req_type && <span style={badgeStyle(reqTypeTone(card.req_type))}>{card.req_type}</span>}
            {card.is_cs && <span style={badgeStyle('warn')}>CS발</span>}
          </div>
          {showReason && (
            <div style={{ marginTop: '4px', color: COLORS.textMuted, fontSize: '12px', lineHeight: 1.45, wordBreak: 'break-word' }}>{card.reason}</div>
          )}
          {showChanges && <ChangeDetails card={card} />}
          <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', color: COLORS.textSub, fontSize: '12px', lineHeight: 1.5 }}>
            <span>{assigneeText(card)}</span>
            <CardDateLine card={card} />
          </div>
        </div>
      </div>
    </li>
  );
}

function visibleSectionCards(meta, cards, expanded) {
  if (expanded) return cards;
  if (meta.key !== 'changed') return cards.slice(0, SECTION_LIMIT);
  const pinned = cards.filter((card) => card?.has_status_transition);
  const rest = cards.filter((card) => !card?.has_status_transition);
  return [...pinned, ...rest.slice(0, SECTION_LIMIT)];
}

function SectionBlock({ system, meta, cards, expanded, onToggle }) {
  const visibleCards = visibleSectionCards(meta, cards, expanded);
  const hiddenCount = Math.max(0, cards.length - visibleCards.length);
  return (
    <section style={{ ...sectionStyle, borderColor: meta.key === 'delayed' ? COLORS.danger : COLORS.borderSoft }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: cards.length ? '8px' : 0 }}>
        <h4 style={{ margin: 0, color: meta.key === 'delayed' ? COLORS.dangerText : COLORS.text, fontSize: '15px', fontWeight: 700, lineHeight: 1.3 }}>
          {meta.label} {cards.length}
        </h4>
        {meta.key === 'delayed' && <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 620 }}>보류 제외</span>}
        {meta.key === 'next_week' && <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 620 }}>지연 제외</span>}
        {meta.key === 'deployed' && <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 620 }}>완료 또는 배포요청일 기준</span>}
      </div>
      {!cards.length ? (
        <div style={{ color: COLORS.textMuted, fontSize: '13px', padding: '10px 0' }}>없음</div>
      ) : (
        <>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {visibleCards.map((card, idx) => (
              <CardRow key={`${system}-${meta.key}-${card.url || card.title || idx}`} card={card} index={idx} sectionKey={meta.key} />
            ))}
          </ol>
          {hiddenCount > 0 && (
            <button type="button" onClick={onToggle} style={{ ...btn('secondary', 'sm'), marginTop: '8px', width: '100%', fontWeight: 620 }}>
              더보기 {hiddenCount}
            </button>
          )}
          {expanded && cards.length > SECTION_LIMIT && (
            <button type="button" onClick={onToggle} style={{ ...btn('secondary', 'sm'), marginTop: '8px', width: '100%', fontWeight: 620 }}>
              접기
            </button>
          )}
        </>
      )}
    </section>
  );
}

function ReassignCardRow({ card, index }) {
  const href = safeUrl(card.url);
  const delay = Number(card.delay_days || 0);
  const systems = (card.systems || []).filter(Boolean).join(' · ') || '시스템 없음';
  const assignees = (card.assignees || []).filter(Boolean);
  const inactiveAssignees = new Set((card.inactive_assignees || []).filter(Boolean));
  return (
    <li style={{ borderTop: index === 0 ? 'none' : `1px solid ${COLORS.borderSoft}`, padding: '10px 0', minWidth: 0 }}>
      <div style={{ display: 'flex', gap: '8px', minWidth: 0 }}>
        <span style={{ width: '22px', flexShrink: 0, textAlign: 'right', color: COLORS.textMuted, fontSize: '12px', fontWeight: 620, lineHeight: 1.55 }}>{index + 1}.</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
            {href ? (
              <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.text, textDecoration: 'none', fontWeight: 620, fontSize: '13px', lineHeight: 1.45, display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <span style={{ wordBreak: 'break-word' }}>{card.title || '(제목 없음)'}</span>
                <IconExternalLink size={13} stroke={1.8} style={{ flexShrink: 0 }} />
              </a>
            ) : (
              <span style={{ color: COLORS.text, fontWeight: 620, fontSize: '13px', lineHeight: 1.45, wordBreak: 'break-word' }}>{card.title || '(제목 없음)'}</span>
            )}
            <span style={badgeStyle(card.is_open_progress ? 'danger' : 'default')}>{card.is_open_progress ? '진행성' : '보류'}</span>
            {delay > 0 && <span style={badgeStyle('danger')}>D+{delay}</span>}
            <span style={badgeStyle(statusTone(card.status))}>{card.status || '상태 없음'}</span>
          </div>
          <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', color: COLORS.textSub, fontSize: '12px', lineHeight: 1.5 }}>
            {assignees.length ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                {assignees.map((name, assigneeIdx) => {
                  const inactive = inactiveAssignees.has(name);
                  return (
                    <span key={`${name}-${assigneeIdx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: inactive ? COLORS.dangerText : COLORS.textSub, fontWeight: inactive ? 620 : 500 }}>
                      {assigneeIdx > 0 && <span style={{ color: COLORS.textMuted, fontWeight: 500 }}>·</span>}
                      <span>{name}</span>
                      {inactive && <span style={badgeStyle('danger')}>이탈</span>}
                    </span>
                  );
                })}
              </span>
            ) : (
              <span>이탈 개발자</span>
            )}
            <span>{systems}</span>
            <CardDateLine card={card} />
          </div>
        </div>
      </div>
    </li>
  );
}

function ReassignSection({ rows, progressCount }) {
  if (!rows?.length) return null;
  const urgent = Number(progressCount || 0) > 0;
  const heading = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: urgent ? '8px' : 0, flexWrap: 'wrap' }}>
      <h3 style={{ margin: 0, color: urgent ? COLORS.dangerText : COLORS.textSub, fontSize: '16px', fontWeight: 700, lineHeight: 1.3 }}>
        🔁 개발자 이탈 · 재배정 필요 {Number(progressCount || 0).toLocaleString()}
      </h3>
      <span style={badgeStyle(urgent ? 'danger' : 'default')}>{urgent ? '진행성' : '보류만'}</span>
    </div>
  );
  const list = (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {rows.map((card, idx) => (
        <ReassignCardRow key={`${card.url || card.title || idx}`} card={card} index={idx} />
      ))}
    </ol>
  );
  if (!urgent) {
    return (
      <details style={{ ...sectionStyle, marginBottom: '16px', background: 'var(--gw-subtle, #fbfbfa)' }}>
        <summary style={{ cursor: 'pointer', listStyle: 'none' }}>{heading}</summary>
        <div style={{ marginTop: '8px' }}>{list}</div>
      </details>
    );
  }
  return (
    <section style={{ ...sectionStyle, marginBottom: '16px', borderColor: COLORS.danger }}>
      {heading}
      {list}
    </section>
  );
}

function SystemColumn({ system, data, expandedSections, toggleSection }) {
  return (
    <div style={{ minWidth: 0 }}>
      <h3 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '17px', fontWeight: 700 }}>{system}</h3>
      <div style={{ display: 'grid', gap: '12px' }}>
        {SECTION_META.map((meta) => {
          const key = `${system}-${meta.key}`;
          return (
            <SectionBlock
              key={key}
              system={system}
              meta={meta}
              cards={data?.[meta.key] || []}
              expanded={!!expandedSections[key]}
              onToggle={() => toggleSection(key)}
            />
          );
        })}
      </div>
    </div>
  );
}

function OpenCardList({ cards }) {
  if (!cards?.length) {
    return <div style={{ color: COLORS.textMuted, fontSize: '12px', marginTop: '8px' }}>없음</div>;
  }
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
      {cards.map((card, idx) => {
        const href = safeUrl(card.url);
        const delay = Number(card.delay_days || 0);
        return (
          <li key={`${card.url || card.title || idx}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', borderTop: idx === 0 ? 'none' : `1px solid ${COLORS.borderSoft}`, padding: '8px 0', fontSize: '12px', minWidth: 0 }}>
            {href ? (
              <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.text, textDecoration: 'none', fontWeight: 620, display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <span style={{ wordBreak: 'break-word' }}>{card.title || '(제목 없음)'}</span>
                <IconExternalLink size={12} stroke={1.8} />
              </a>
            ) : (
              <span style={{ color: COLORS.text, fontWeight: 620, wordBreak: 'break-word' }}>{card.title || '(제목 없음)'}</span>
            )}
            {delay > 0 && <span style={badgeStyle('danger')}>D+{delay}</span>}
            <span style={badgeStyle(statusTone(card.status))}>{card.status || '상태 없음'}</span>
          </li>
        );
      })}
    </ul>
  );
}

function personSystemText(row) {
  if (row?.by_system) {
    const one = Number(row.by_system['샵메이트 1.0'] || 0).toLocaleString();
    const two = Number(row.by_system['샵메이트 2.0'] || 0).toLocaleString();
    return `1.0 ${one}건 / 2.0 ${two}건`;
  }
  return row?.system || '기타';
}

function DeveloperRow({ row }) {
  const systemText = personSystemText(row);
  const systemTone = row.by_system ? 'default' : (row.system?.includes('2.0') ? 'info' : 'default');
  return (
    <details style={{ borderTop: `1px solid ${COLORS.borderSoft}`, padding: '10px 0' }}>
      <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
          <IconChevronDown size={15} stroke={2} style={{ color: COLORS.textMuted, flexShrink: 0 }} />
          <strong style={{ color: COLORS.text, fontSize: '13px' }}>{row.name}</strong>
          {row.inactive && <span style={badgeStyle('default')}>(이탈)</span>}
          <span style={badgeStyle(systemTone)}>{systemText}</span>
          <span style={{ color: COLORS.textSub, fontSize: '12px' }}>이번주 처리 {Number(row.week_touched || 0).toLocaleString()}</span>
          <span style={{ color: COLORS.textSub, fontSize: '12px' }}>현재 담당 {Number(row.open_now || 0).toLocaleString()}</span>
          <span style={{ color: Number(row.delayed_now || 0) ? COLORS.dangerText : COLORS.textSub, fontSize: '12px', fontWeight: Number(row.delayed_now || 0) ? 620 : 500 }}>지연 {Number(row.delayed_now || 0).toLocaleString()}</span>
        </div>
      </summary>
      <OpenCardList cards={row.cards_open || []} />
    </details>
  );
}

function DeveloperGroup({ title, rows }) {
  return (
    <section style={sectionStyle}>
      <h3 style={{ margin: '0 0 4px', color: COLORS.text, fontSize: '15px', fontWeight: 700 }}>{title}</h3>
      {(rows || []).map((row) => <DeveloperRow key={row.name} row={row} />)}
    </section>
  );
}

function SummarySection({ summary, loading, error, generating, open, toggleOpen, onGenerate }) {
  const busy = generating || summary?.status === 'generating';
  const failed = summary?.status === 'failed';
  return (
    <section style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.lg, padding: '16px 18px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button type="button" onClick={toggleOpen} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: COLORS.text, display: 'inline-flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
          {open ? <IconChevronDown size={17} stroke={2} /> : <IconChevronRight size={17} stroke={2} />}
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 620, color: COLORS.text }}>📋 주간 요약</h3>
        </button>
        {summary?.generated_at && (
          <span style={{ fontSize: '12px', color: COLORS.textSub }}>생성 {formatSyncedAt(summary.generated_at)}</span>
        )}
        {summary?.week_label && (
          <span style={{ ...badge('blue') }}>{summary.week_label}</span>
        )}
        {busy && (
          <span style={{ ...badge('yellow'), gap: '4px' }}>
            <IconLoader2 size={12} stroke={2} style={{ animation: 'dx-summary-spin .8s linear infinite' }} />생성 중
          </span>
        )}
        <button type="button" onClick={onGenerate} disabled={busy} style={{ ...btn('accent', 'sm'), marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: busy ? 0.65 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy && <IconLoader2 size={14} stroke={2} style={{ animation: 'dx-summary-spin .8s linear infinite' }} />}
          {summary ? '다시 요약' : '지금 생성'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: '14px' }}>
          {loading && <div style={{ color: COLORS.textSub, fontSize: '13px' }}>요약을 불러오는 중입니다.</div>}
          {!loading && error && <div style={{ color: COLORS.dangerText, background: COLORS.dangerSoft, borderRadius: RADIUS.lg, padding: '10px 12px', fontSize: '13px' }}>{error}</div>}
          {!loading && !error && !summary && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>아직 생성된 요약이 없습니다.</div>}
          {!loading && !error && summary && (
            <>
              {failed && <div style={{ color: COLORS.dangerText, background: COLORS.dangerSoft, borderRadius: RADIUS.lg, padding: '10px 12px', fontSize: '13px', marginBottom: '12px' }}>{summary.error || '요약 생성에 실패했습니다.'}</div>}
              {summary.overall_brief && (
                <div style={{ marginBottom: 0 }}>
                  <Markdown>{summary.overall_brief}</Markdown>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default function DxWeekly({ refreshKey = 0 }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const syncPollRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    dxBoardAPI.getWeekly(weekOffset)
      .then((r) => setData(r.data || null))
      .catch((e) => {
        setData(null);
        setError(e.response?.data?.detail || '주간회의 데이터를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  }, [weekOffset]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const loadSummary = useCallback((quiet = false) => {
    if (!quiet) setSummaryLoading(true);
    setSummaryError('');
    return dxBoardAPI.getSummary()
      .then((r) => setSummary(r.data?.summary || null))
      .catch((e) => setSummaryError(e.response?.data?.detail || '주간 요약을 불러오지 못했습니다.'))
      .finally(() => {
        if (!quiet) setSummaryLoading(false);
      });
  }, []);

  useEffect(() => { loadSummary(false); }, [loadSummary, refreshKey]);

  useEffect(() => {
    if (summary?.status !== 'generating') return undefined;
    const timer = setInterval(() => loadSummary(true), 4000);
    return () => clearInterval(timer);
  }, [loadSummary, summary?.status]);

  const clearSyncPoll = useCallback(() => {
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
  }, []);

  useEffect(() => () => clearSyncPoll(), [clearSyncPoll]);

  const generateSummary = useCallback(() => {
    setSummaryGenerating(true);
    setSummaryError('');
    dxBoardAPI.generateSummary()
      .then((r) => {
        setSummary(r.data?.summary || { status: r.data?.status || 'generating' });
        return loadSummary(true);
      })
      .catch((e) => setSummaryError(e.response?.data?.detail || '주간 요약 생성을 시작하지 못했습니다.'))
      .finally(() => setSummaryGenerating(false));
  }, [loadSummary]);

  const syncAnalytics = useCallback(() => {
    if (syncing) return;
    const prevSynced = data?.synced_at || null;
    let attempts = 0;
    clearSyncPoll();
    setSyncing(true);
    setSyncMsg('카드 수집 요청 중');
    dxBoardAPI.syncAnalytics()
      .then(() => {
        setSyncMsg('카드 수집 중');
        syncPollRef.current = setInterval(() => {
          attempts += 1;
          dxBoardAPI.getWeekly(weekOffset)
            .then((r) => {
              const nextData = r.data || null;
              const nextSynced = nextData?.synced_at || null;
              if (nextSynced !== prevSynced) {
                clearSyncPoll();
                setData(nextData);
                loadSummary(true);
                setSyncing(false);
                setSyncMsg('카드 수집 완료');
                return;
              }
              if (attempts >= SYNC_POLL_MAX_ATTEMPTS) {
                clearSyncPoll();
                setSyncing(false);
                setSyncMsg('수집 지연, 잠시 후 새로고침');
              }
            })
            .catch((e) => {
              if (attempts >= SYNC_POLL_MAX_ATTEMPTS) {
                clearSyncPoll();
                setSyncing(false);
                setSyncMsg(e.response?.data?.detail || '수집 지연, 잠시 후 새로고침');
              }
            });
        }, SYNC_POLL_INTERVAL_MS);
      })
      .catch((e) => {
        clearSyncPoll();
        setSyncing(false);
        setSyncMsg(e.response?.data?.detail || '카드 수집을 시작하지 못했습니다.');
      });
  }, [clearSyncPoll, data?.synced_at, loadSummary, syncing, weekOffset]);

  const developerGroups = useMemo(() => data?.by_developer || {}, [data]);
  const qaRows = useMemo(() => data?.by_qa || [], [data]);
  const reassignRows = useMemo(() => data?.reassign || [], [data]);
  const reassignProgressCount = Number(data?.reassign_progress_count || 0);
  const toggleSection = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.lg, padding: '42px', textAlign: 'center', color: COLORS.textSub, fontSize: '14px' }}>
        <IconLoader2 size={18} stroke={2} style={{ animation: 'dx-summary-spin .8s linear infinite', verticalAlign: '-4px', marginRight: '6px' }} />
        주간회의 데이터를 불러오는 중입니다.
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: COLORS.dangerSoft, border: `1px solid ${COLORS.dangerSoft}`, borderRadius: RADIUS.lg, padding: '28px', textAlign: 'center', color: COLORS.dangerText, fontSize: '14px' }}>
        <div style={{ marginBottom: '12px' }}>{error}</div>
        <button type="button" onClick={load} style={btn('secondary', 'sm')}>다시 불러오기</button>
      </div>
    );
  }

  return (
    <div>
      <section style={{ ...sectionStyle, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: 700, lineHeight: 1.4 }}>회의일 {formatDate(data?.meeting_date)}</div>
          <div style={{ color: COLORS.textSub, fontSize: '12px', marginTop: '3px' }}>
            주간 {formatDate(data?.week_start)}~{formatDate(data?.week_end)} · 동기화 {formatSyncedAt(data?.synced_at)} · CS발 진행중 {Number(data?.cs_open || 0).toLocaleString()} · {formatReqTypeCoverage(data?.req_type_coverage)}
          </div>
          <KpiBand data={data} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexWrap: 'wrap' }}>
          <button type="button" onClick={syncAnalytics} disabled={syncing} style={{ ...btn('secondary', 'sm'), display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 620, opacity: syncing ? 0.7 : 1, cursor: syncing ? 'not-allowed' : 'pointer' }}>
            <span aria-hidden="true">🔄</span>
            {syncing && <IconLoader2 size={14} stroke={2} style={{ animation: 'dx-summary-spin .8s linear infinite' }} />}
            카드 수집
          </button>
          {syncMsg && <span style={{ color: COLORS.textSub, fontSize: '12px', fontWeight: 700 }}>{syncMsg}</span>}
          <button type="button" onClick={() => setWeekOffset((value) => value - 1)} style={{ ...btn('secondary', 'sm'), display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 620 }}>
            <IconChevronLeft size={15} stroke={2} />이전주
          </button>
          <button type="button" onClick={() => setWeekOffset((value) => Math.min(0, value + 1))} disabled={weekOffset >= 0} style={{ ...btn('secondary', 'sm'), display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 620, opacity: weekOffset >= 0 ? 0.55 : 1, cursor: weekOffset >= 0 ? 'not-allowed' : 'pointer' }}>
            다음주<IconChevronRight size={15} stroke={2} />
          </button>
        </div>
      </section>

      <SummarySection
        summary={summary}
        loading={summaryLoading}
        error={summaryError}
        generating={summaryGenerating}
        open={summaryOpen}
        toggleOpen={() => setSummaryOpen((value) => !value)}
        onGenerate={generateSummary}
      />

      <ReassignSection rows={reassignRows} progressCount={reassignProgressCount} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', alignItems: 'start' }}>
        {SYSTEMS.map((system) => (
          <SystemColumn
            key={system}
            system={system}
            data={data?.systems?.[system]}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
          />
        ))}
      </div>

      <section style={{ marginTop: '18px' }}>
        <h3 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '17px', fontWeight: 700 }}>개발자별 현황</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
          <DeveloperGroup title="샵메이트 1.0" rows={developerGroups['샵메이트 1.0'] || []} />
          <DeveloperGroup title="샵메이트 2.0" rows={developerGroups['샵메이트 2.0'] || []} />
        </div>
        <div style={{ marginTop: '12px' }}>
          <DeveloperGroup title="🧪 QA (아이니)" rows={qaRows} />
        </div>
      </section>
    </div>
  );
}
